import { useEffect } from 'react';
import dayjs from 'dayjs';
import useAssetCancellationRequestCreate from 'modules/queries/asset/cancellation/request/useAssetCancellationRequestCreate';
import useSupportRequestDetails from 'modules/queries/support/request/useDetails';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import CheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/Check';
import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardHeader from '@liquidweb-dev/undercurrent/CardHeader';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import Chip, { type ChipProps } from '@liquidweb-dev/undercurrent/Chip';
import Link from '@liquidweb-dev/undercurrent/Link';
import { Link as RouterLink } from 'react-router-dom';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import CancellationModalHeader from './CancellationModalHeader';
import CancellationLoader from './CancellationLoader';
import { TICKET_STATUS, type CancellationFormData } from './constants';

const getTicketStatusConfig = (
	status?: string,
): { label: string; color: ChipProps['color'] } => {
	switch (status) {
		case TICKET_STATUS.WAITING_ON_CUSTOMER:
			return { label: 'Needs attention', color: 'warning' };
		case TICKET_STATUS.NEW:
		case TICKET_STATUS.OPEN:
			return { label: status, color: 'success' };
		default:
			return { label: 'Closed', color: 'default' };
	}
};

const getLoaderText = (
	ticketId: string | undefined,
	isTicketCreationSuccess: boolean,
) => {
	if (isTicketCreationSuccess) {
		return 'Finishing up...';
	}
	if (!ticketId) {
		return "We're creating your cancellation case...";
	}
	return 'Fetching details...';
};

interface TicketKeyItemProps extends BoxProps {
	label: string;
}

const TicketKeyItem = ({ label, children, ...rest }: TicketKeyItemProps) => {
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				minWidth: '96px',
				alignItems: 'flex-start',
				gap: 50,
				flex: '1 0 0',
			}}
			{...rest}
		>
			<Typography
				variant="label-lg"
				sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
			>
				{label}
			</Typography>
			{children}
		</Box>
	);
};

interface TicketDetailsProps {
	ticketId: string | undefined;
	status: string | undefined;
	subject: string | undefined;
	product: string | undefined;
	createdAt: string | undefined;
}

const TicketDetails = ({
	ticketId,
	status,
	subject,
	product,
	createdAt,
}: TicketDetailsProps) => {
	const { label, color } = getTicketStatusConfig(status);
	const domain = subject?.substring(25);

	return (
		<Stack
			sx={{
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				gap: 150,
				alignSelf: 'stretch',
			}}
		>
			<Stack
				sx={{
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'flex-start',
					gap: 50,
					width: '100%',
				}}
			>
				<TypographyOverflow
					variant="title-xs"
					sx={{ fontWeight: 500, width: '100%' }}
				>
					Cancellation Assistance : {domain}
				</TypographyOverflow>
				<Typography
					variant="body-sm"
					sx={({ vars }) => ({ color: vars.palette.uc.text.medium })}
				>
					<Link
						component={RouterLink}
						to={`/support/mycases/${ticketId}`}
						sx={{ textDecoration: 'none' }}
					>
						Case number {ticketId}
					</Link>
				</Typography>
			</Stack>
			<Stack
				sx={{
					height: '42px',
					alignItems: 'flex-start',
					alignContent: 'space-between',
					gap: 300,
					flexWrap: 'wrap',
					width: '100%',
				}}
			>
				<TicketKeyItem label="Status">
					<Chip size="small" color={color} label={label} />
				</TicketKeyItem>
				<TicketKeyItem label="Product">
					<Typography variant="body-sm">{product}</Typography>
				</TicketKeyItem>
				<TicketKeyItem label="Created">
					<Tooltip title={`Created ${dayjs(createdAt).format('l LT')}`}>
						<Typography variant="body-sm">
							{dayjs(createdAt).format('l')}
						</Typography>
					</Tooltip>
				</TicketKeyItem>
			</Stack>
		</Stack>
	);
};

export interface SubmittedRequestProps {
	uniqId: string;
	ticketId: string | undefined;
	formValues: CancellationFormData;
	assetDetails: AssetDetails | undefined;
	isAssetDetailsLoading: boolean;
	onCancelWithoutAssistance: () => void;
}

const SubmittedRequest = ({
	uniqId,
	ticketId,
	formValues,
	assetDetails,
	isAssetDetailsLoading,
	onCancelWithoutAssistance,
}: SubmittedRequestProps) => {
	const { data: ticketDetails, isLoading: isTicketDetailsLoading } =
		useSupportRequestDetails(
			{ ticket_number: ticketId },
			{ enabled: Boolean(ticketId) },
		);

	const { displayType } = assetDetails || {};

	const {
		mutate: createCancellationTicket,
		isIdle: isTicketCreationIdle,
		isLoading: isTicketCreationOnProgress,
		isSuccess: isTicketCreationSuccess,
	} = useAssetCancellationRequestCreate();

	const {
		status,
		subject,
		created_date: ticketCreatedAt,
	} = ticketDetails || {};

	const loadStatus = getLoaderText(ticketId, isTicketCreationSuccess);

	const loadIcon = isTicketCreationSuccess ? (
		<CheckIcon
			sx={(theme) => ({
				width: theme.size.md,
				height: theme.size.md,
				color: theme.vars.palette.uc.success.icon.main,
			})}
		/>
	) : (
		<LoaderCircleIcon
			sx={(theme) => ({
				width: theme.size.md,
				height: theme.size.md,
			})}
		/>
	);

	useEffect(() => {
		if (!ticketId && isTicketCreationIdle) {
			createCancellationTicket({
				uniq_id: uniqId,
				cancellation_reason: formValues.cancellation_reason,
				additional_information: formValues.additional_info,
			});
		}
	}, [
		ticketId,
		createCancellationTicket,
		uniqId,
		formValues,
		isTicketCreationIdle,
	]);

	return (
		<Stack
			sx={{
				flexDirection: 'column',
				alignItems: 'center',
				gap: 300,
				alignSelf: 'stretch',
			}}
		>
			<CancellationModalHeader>
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						width: '100%',
						justifyContent: 'center',
					}}
				>
					<Typography
						variant="title-sm"
						sx={[
							{
								fontWeight: 500,
							},
							(theme) => ({ color: theme.vars.palette.uc.text.main }),
						]}
					>
						We&apos;ve received your cancellation request
					</Typography>
				</Stack>
				<Typography
					variant="body-sm"
					sx={{
						textAlign: 'center',
					}}
				>
					We&apos;ve received your request and a specialist will reach out
					within 24-48 hours to help finalize the termination of your service.
					You can view or manage your request at any time using the case below.
				</Typography>
			</CancellationModalHeader>
			<Card sx={{ width: '100%' }}>
				<CardHeader title="Your cancellation case" />
				<CardContent>
					{isTicketCreationOnProgress ||
					isTicketDetailsLoading ||
					isAssetDetailsLoading ? (
						<CancellationLoader icon={loadIcon} title={loadStatus} />
					) : (
						<TicketDetails
							ticketId={ticketId}
							status={status}
							subject={subject}
							product={displayType}
							createdAt={ticketCreatedAt}
						/>
					)}
				</CardContent>
			</Card>
			{!ticketId && (
				<Stack
					sx={{
						display: 'flex',
						alignItems: 'center',
						alignSelf: 'stretch',
					}}
				>
					<Button
						size="large"
						variant="secondary"
						sx={{
							paddingY: 100,
							paddingX: 200,
						}}
						onClick={onCancelWithoutAssistance}
						fullWidth
					>
						Cancel without assistance
					</Button>
				</Stack>
			)}
		</Stack>
	);
};

export default SubmittedRequest;
